
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { useSecureVote } from '@/hooks/useSecureVote';
import { useSecureIPHash } from '@/hooks/useSecureIPHash';
import { sanitizeInput } from '@/utils/securityValidation';
import { useSecurityAudit } from '@/hooks/useSecurityAudit';
import { useMTCaptcha } from '@/hooks/useMTCaptcha';
import { useAuth } from '@/hooks/useAuth';
import { useVoteCooldown } from '@/hooks/useVoteCooldown';
import { useLanguage } from '@/contexts/LanguageContext';

interface VoteFormProps {
  serverId: string;
  serverName: string;
  gameType: string;
  isOpen: boolean;
  onClose: () => void;
  defaultPlayerName?: string;
  lockPlayer?: boolean;
}

export const VoteForm: React.FC<VoteFormProps> = ({
  serverId,
  serverName,
  gameType,
  isOpen,
  onClose,
  defaultPlayerName = '',
  lockPlayer = false
}) => {
  // Initialiser avec defaultPlayerName ou récupérer depuis localStorage
  const [playerName, setPlayerName] = useState(() => {
    if (defaultPlayerName) return defaultPlayerName;
    try {
      return localStorage.getItem('vote-player-name') || '';
    } catch {
      return '';
    }
  });
  const { t } = useLanguage();
  
  const secureVoteMutation = useSecureVote();
  const { ipHash, ipHashAlt, realIP, realIPAlt, isLoading: ipLoading } = useSecureIPHash();
  const { logSecurityEvent } = useSecurityAudit();
  
  const {
    captchaContainerRef,
    error,
    isLoading: captchaLoading,
    setError,
    getVerifiedToken,
    resetCaptcha,
    captchaKey
  } = useMTCaptcha(isOpen);

  const { isLoading: cooldownLoading, eligible, secondsLeft } = useVoteCooldown(serverId);

  const formatRemaining = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}h ${m.toString().padStart(2,'0')}m`;
    if (m > 0) return `${m}m ${s.toString().padStart(2,'0')}s`;
    return `${s}s`;
  };

  const getPlaceholder = () => {
    switch (gameType.toLowerCase()) {
      case 'minecraft':
        return t('vote.placeholderMinecraft');
      case 'dofus':
        return t('vote.placeholderDofus');
      case 'counter-strike':
        return t('vote.placeholderCS');
      default:
        return t('vote.placeholderDefault');
    }
  };

  const getLabel = () => {
    switch (gameType.toLowerCase()) {
      case 'minecraft':
        return t('vote.labelMinecraft');
      case 'dofus':
        return t('vote.labelDofus');
      case 'counter-strike':
        return t('vote.labelCS');
      default:
        return t('vote.labelDefault');
    }
  };

  // Sauvegarder le pseudo dans localStorage quand il change (sauf si verrouillé)
  const handlePlayerNameChange = (value: string) => {
    setPlayerName(value);
    if (!lockPlayer && value.trim() && value.trim() !== defaultPlayerName) {
      try {
        localStorage.setItem('vote-player-name', value.trim());
      } catch {
        // Ignore localStorage errors
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    //console.log('🚀 Secure vote submission started', { serverId, playerName: playerName.trim() });
    
    // Enhanced validation
    const sanitizedPlayerName = sanitizeInput(playerName.trim());
    if (sanitizedPlayerName.length < 2 || sanitizedPlayerName.length > 50) {
      setError(t('vote.error.pseudoLength'));
      return;
    }

    // Check for potentially malicious input
    if (sanitizedPlayerName !== playerName.trim()) {
      logSecurityEvent({
        eventType: 'suspicious_input_detected',
        eventData: { 
          originalInput: playerName.trim(),
          sanitizedInput: sanitizedPlayerName,
          serverId 
        }
      });
      setError(t('vote.error.invalidCharacters'));
      return;
    }

    if (ipLoading || !ipHash) {
      setError(t('vote.error.loading'));
      return;
    }

    // CORRECTION: Vérifier le token CAPTCHA de manière stricte
    const captchaToken = getVerifiedToken();
    console.log('🎯 CAPTCHA token verification result:', { 
      hasToken: !!captchaToken,
      tokenLength: captchaToken?.length || 0 
    });
    
    if (!captchaToken) {
      console.error('❌ No CAPTCHA token available - BLOCKING vote');
      setError(t('vote.error.captchaRequired') || 'Veuillez compléter le CAPTCHA');
      
      // Log for security monitoring
      logSecurityEvent({
        eventType: 'vote_blocked_no_captcha',
        eventData: { serverId, reason: 'No CAPTCHA token available' }
      });
      return; // BLOQUER le vote sans CAPTCHA
    }

    console.log('✅ All validations passed, submitting secure vote...');

    try {
      await secureVoteMutation.mutateAsync({
        serverId,
        playerName: sanitizedPlayerName,
        captchaToken: captchaToken,
        ipHash,
        ipHashAlt,
        realIP,
        realIPAlt,
      });
      
      onClose();
    } catch (error) {
      console.error('❌ Secure vote error:', error);
      // Reset captcha on error to prevent reuse
      resetCaptcha();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="player-name" className="text-gray-300 text-base">
          {getLabel()}
        </Label>
        <Input
          id="player-name"
          type="text"
          placeholder={getPlaceholder()}
          value={playerName}
          onChange={(e) => handlePlayerNameChange(e.target.value)}
          className="modern-input text-base min-h-[44px]"
          readOnly={lockPlayer}
          required
        />
        {!lockPlayer && (
          <p className="text-xs sm:text-sm text-amber-300 mt-1">
            {t('vote.nicknameNotValidated')}
          </p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label className="text-gray-300 text-base">{t('vote.securityVerification')}</Label>
        <div className="bg-gray-800 p-3 sm:p-4 rounded-lg border border-gray-600 min-h-[140px] sm:min-h-[180px] flex items-center justify-center">
          <div 
            key={captchaKey}
            ref={captchaContainerRef} 
            className="w-full flex justify-center items-center min-h-[120px] sm:min-h-[140px]"
          >
            {captchaLoading && (
              <div className="text-gray-400 text-sm">{t('vote.captchaLoading')}</div>
            )}
          </div>
        </div>
      </div>
      
      {error && (
        <div className="text-red-400 text-sm text-center bg-red-500/10 border border-red-500/20 rounded p-2">
          {error}
        </div>
      )}
      {(cooldownLoading || !eligible) && (
        <div className="text-sm text-amber-300 text-center">
          {cooldownLoading ? t('vote.checkingActivity') : t('vote.cooldownMessage', { time: formatRemaining(secondsLeft) })}
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="button"
          onClick={() => {
            onClose();
            window.location.reload();
          }}
          className="btn-secondary flex-1 min-h-[44px]"
        >
          {t('button.refresh')}
        </Button>
        <Button
          type="submit"
          className="btn-primary flex-1 min-h-[44px]"
          disabled={secureVoteMutation.isPending || ipLoading}
        >
          {secureVoteMutation.isPending ? t('vote.verifyingInProgress') : t('vote.voteButton')}
        </Button>
      </div>
    </form>
  );
};
